import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type Theme = "dark" | "light";

export interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
}

// ─── Token maps ───────────────────────────────────────────────────────────────

const DARK_TOKENS: Record<string, string> = {
  "--surface":       "#111420",
  "--surface2":      "#161614",
  "--card":          "#111420",
  "--bg":            "#0b0d14",
  "--card-solid":    "#1a1917",
  "--input-bg":      "rgba(255,255,255,0.035)",
  "--section-header":"rgba(255,255,255,0.02)",
  "--spec-bg":       "rgba(255,255,255,0.02)",
  "--border":        "rgba(255,255,255,0.08)",
  "--border-strong": "rgba(255,255,255,0.16)",
  "--row-hover":     "rgba(255,255,255,0.025)",
  "--text":          "#e8e6de",
  "--text2":         "#b4b2a9",
  "--text3":         "#6a6a65",
  "--or":            "#BF3D0C",
  "--or2":           "#E8593C",
  "--or-dim":        "rgba(191,61,12,0.10)",
  "--or-dim-strong": "rgba(191,61,12,0.18)",
  "--red":           "#e05252",
  "--shadow":        "0 4px 24px rgba(0,0,0,0.5)",
  "--shadow-sm":     "0 2px 8px rgba(0,0,0,0.4)",
  "--topbar-h":      "56px",
  "--scrollbar":     "rgba(255,255,255,0.08)",
};

const LIGHT_TOKENS: Record<string, string> = {
  "--surface":       "#ffffff",
  "--surface2":      "#ffffff",
  "--card":          "#ffffff",
  "--bg":            "#f9f8f6",
  "--card-solid":    "#ffffff",
  "--input-bg":      "#f9f8f6",
  "--section-header":"rgba(0,0,0,0.02)",
  "--spec-bg":       "rgba(0,0,0,0.02)",
  "--border":        "rgba(0,0,0,0.09)",
  "--border-strong": "rgba(0,0,0,0.18)",
  "--row-hover":     "rgba(0,0,0,0.02)",
  "--text":          "#1a1917",
  "--text2":         "#4a4845",
  "--text3":         "#9a9893",
  "--or":            "#BF3D0C",
  "--or2":           "#E8593C",
  "--or-dim":        "rgba(191,61,12,0.08)",
  "--or-dim-strong": "rgba(191,61,12,0.15)",
  "--red":           "#d93232",
  "--shadow":        "0 4px 24px rgba(0,0,0,0.10)",
  "--shadow-sm":     "0 2px 8px rgba(0,0,0,0.08)",
  "--topbar-h":      "56px",
  "--scrollbar":     "rgba(0,0,0,0.12)",
};

// ─── Token injection ──────────────────────────────────────────────────────────

function applyTokens(tokens: Record<string, string>) {
  const root = document.documentElement;
  Object.entries(tokens).forEach(([key, val]) => {
    root.style.setProperty(key, val);
  });
}

const STORAGE_KEY = "aural-theme";

function resolveInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (stored === "dark" || stored === "light") return stored;
  } catch {
    // localStorage unavailable (SSR / private browsing)
  }
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(resolveInitialTheme);

  // Apply tokens + data-theme attribute whenever theme changes
  useEffect(() => {
    const tokens = theme === "dark" ? DARK_TOKENS : LIGHT_TOKENS;
    applyTokens(tokens);
    document.documentElement.setAttribute("data-theme", theme);
    // Persist preference
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* noop */ }
  }, [theme]);

  // Listen for OS-level preference changes (only when user hasn't overridden)
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setThemeState(e.matches ? "dark" : "light");
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggleTheme = useCallback(() => setThemeState((prev) => (prev === "dark" ? "light" : "dark")), []);

  const value: ThemeContextValue = {
    theme,
    isDark: theme === "dark",
    toggleTheme,
    setTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}

// ─── Token exports (for typed access in components) ───────────────────────────

export { DARK_TOKENS, LIGHT_TOKENS };